import { Stack } from "@mui/material";
import { type ClipboardScoreLog } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import { type ClipboardScorePolicy } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { TimelinessAdjustmentInfoAlert } from "@src/appV2/redesign/ClipboardScore/components/TimelinessAdjustmentInfoAlert";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { compareAsc, format, parseISO } from "date-fns";
import { groupBy } from "lodash";
import { useState } from "react";

import { ClipboardScoreHistorySection } from "./Section";

interface ClipboardScoreHistoryContentProps {
  logs: ClipboardScoreLog[];
  isDynamicPenaltiesEnabled: boolean;
  useExpectedArrivalTime: boolean;
  policy?: ClipboardScorePolicy;
  isEmergencyCreditsEnabled?: boolean;
  userId: string;
}

export function ClipboardScoreHistoryContent({
  logs,
  isDynamicPenaltiesEnabled,
  useExpectedArrivalTime,
  policy,
  isEmergencyCreditsEnabled,
  userId,
}: ClipboardScoreHistoryContentProps) {
  const groupedByDate = groupBy(logs, ({ createdAt }) => format(new Date(createdAt), "yyyy-MM-dd"));
  const sortedGroupedByDate = Object.entries(groupedByDate).sort((a, b) =>
    compareAsc(parseISO(b[0]), parseISO(a[0]))
  );

  const [expandedRow, setExpandedRow] = useState<string | false>(false);

  const onClick = (id: string, isExpanded: boolean) => {
    logEvent(APP_V2_USER_EVENTS.CLIPBOARD_SCORE_LOG_TOGGLED, {
      workerId: userId,
      scoreLogId: id,
      action: isExpanded ? "collapse" : "expand",
    });
    setExpandedRow(isExpanded ? false : id);
  };

  return (
    <Stack
      direction="column"
      spacing={8}
      sx={{
        paddingY: 3,
      }}
    >
      <TimelinessAdjustmentInfoAlert />
      {sortedGroupedByDate.map(([date, logs]) => {
        return (
          <ClipboardScoreHistorySection
            key={`history-section-${date}`}
            date={date}
            logs={logs}
            isDynamicPenaltiesEnabled={isDynamicPenaltiesEnabled}
            useExpectedArrivalTime={useExpectedArrivalTime}
            expandedRow={expandedRow}
            policy={policy}
            isEmergencyCreditsEnabled={isEmergencyCreditsEnabled}
            onClick={onClick}
          />
        );
      })}
    </Stack>
  );
}
